import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';
import { applyConsent, initConsentDefaults } from '@/lib/analytics';

interface ConsentData {
  analytics_consent: boolean;
  performance_consent: boolean;
  marketing_consent: boolean;
}

const CONSENT_STORAGE_KEY = 'luvall-privacy-consent';
const LEGACY_CONSENT_STORAGE_KEY = 'privacy_consent';

const normalize = (raw: any): ConsentData => ({
  analytics_consent: !!raw?.analytics_consent,
  performance_consent: !!raw?.performance_consent,
  marketing_consent: !!raw?.marketing_consent,
});

const getLocalStorageConsent = (): ConsentData | null => {
  try {
    const stored =
      localStorage.getItem(CONSENT_STORAGE_KEY) ??
      localStorage.getItem(LEGACY_CONSENT_STORAGE_KEY);
    if (stored) return normalize(JSON.parse(stored));
  } catch (error) {
    console.error('Error reading consent from localStorage:', error);
  }
  return null;
};

const setLocalStorageConsent = (consent: ConsentData) => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch (error) {
    console.error('Error saving consent to localStorage:', error);
  }
};

const clearLocalStorageConsent = () => {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    localStorage.removeItem(LEGACY_CONSENT_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing consent from localStorage:', error);
  }
};

export const useAnalyticsConsent = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [localConsent, setLocalConsent] = useState<ConsentData | null>(null);

  // Consent Mode v2 defaults (denied) must be in place before anything loads
  useEffect(() => {
    initConsentDefaults();
  }, []);

  // Load localStorage consent on mount (guests)
  useEffect(() => {
    if (!user?.id) {
      setLocalConsent(getLocalStorageConsent());
    }
  }, [user?.id]);

  const { data: consent, isLoading } = useQuery({
    queryKey: ['analytics-consent', user?.id],
    queryFn: async (): Promise<ConsentData | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('analytics_user_consent')
        .select('analytics_consent, performance_consent, marketing_consent')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching consent:', error);
        return null;
      }

      return data ? normalize(data) : null;
    },
    enabled: !!user?.id,
  });

  // Migrate localStorage consent to database when the user logs in
  useEffect(() => {
    const migrateConsentToDatabase = async () => {
      if (user?.id && localConsent && !consent) {
        try {
          const { error } = await supabase
            .from('analytics_user_consent')
            .upsert(
              {
                user_id: user.id,
                ...localConsent,
                consent_date: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              },
              { onConflict: 'user_id' },
            );

          if (!error) {
            clearLocalStorageConsent();
            setLocalConsent(null);
            queryClient.invalidateQueries({ queryKey: ['analytics-consent', user.id] });
          }
        } catch (error) {
          console.error('Error migrating consent to database:', error);
        }
      }
    };

    migrateConsentToDatabase();
  }, [user?.id, localConsent, consent, queryClient]);

  const updateConsentMutation = useMutation({
    mutationFn: async (consentData: ConsentData) => {
      if (!user?.id) {
        // Guests: store locally
        setLocalStorageConsent(consentData);
        setLocalConsent(consentData);
        return;
      }

      const { error } = await supabase
        .from('analytics_user_consent')
        .upsert(
          {
            user_id: user.id,
            ...consentData,
            consent_date: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' },
        );

      if (error) throw error;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ['analytics-consent', user?.id] });
      }
    },
  });

  const updateConsent = (consentData: ConsentData) => {
    const normalized = normalize(consentData);
    // Reflect the decision in Google Analytics immediately
    applyConsent({
      analytics: normalized.analytics_consent,
      marketing: normalized.marketing_consent,
    });
    updateConsentMutation.mutate(normalized);
  };

  // Effective consent: DB for authenticated users, localStorage for guests
  const effectiveConsent = user?.id ? consent : localConsent;

  const hasAnalyticsConsent = effectiveConsent?.analytics_consent ?? false;
  const hasPerformanceConsent = effectiveConsent?.performance_consent ?? false;
  const hasMarketingConsent = effectiveConsent?.marketing_consent ?? false;

  // Keep GA in sync with the stored decision (e.g. on reload / after login)
  useEffect(() => {
    if (!effectiveConsent) return;
    applyConsent({
      analytics: effectiveConsent.analytics_consent,
      marketing: effectiveConsent.marketing_consent,
    });
  }, [effectiveConsent?.analytics_consent, effectiveConsent?.marketing_consent]);

  return {
    consent: effectiveConsent,
    hasAnalyticsConsent,
    hasPerformanceConsent,
    hasMarketingConsent,
    updateConsent,
    isLoading: user?.id ? isLoading : false,
    isUpdating: updateConsentMutation.isPending,
  };
};
